/**
 * One rule, two screens: damaged stock stays separate from good stock.
 *
 * Damage is the exception a yard cannot afford to lose track of. Damaged units
 * belong to a claim — against a carrier, a customer, or the yard itself — and
 * that claim is only worth anything while the units are still identifiable.
 * The moment damaged and undamaged quantities sit in one number, nobody can
 * say how many were damaged or who owed for them.
 *
 * Two places can break that, and both live here so they cannot drift apart:
 *
 *   - the outbound pick dialog, where a damaged row must not be picked onto a
 *     shipment by accident (getVisibleInventoryItems / excludeDamagedInventories)
 *   - relocate, where moving stock into an occupied unmanaged location merges
 *     two inventoryItems into one row (canMergeInventoryItems)
 *   - bulk tanks, where the commodity is indivisible and placing damaged and
 *     clean stock in one tank contaminates the whole pool, and the tank stays
 *     contaminated until a manager clears it (canStoreInBulkTank)
 */

/**
 * The inventoryItems of an inventory that the pick dialog should show.
 *
 * Damaged stock is hidden by default. A manager building an outbound is picking
 * sellable goods, and damaged inventory belongs to a claim, not a shipment —
 * showing it in the picker invites an accidental pick. This is a view filter,
 * not a block: "Show damaged" brings the rows back when the pick is deliberate.
 *
 * The filter is per inventoryItem, not per inventory. An inventory rolls up
 * isDamaged from any one of its items, so filtering on the parent would hide
 * good stock stored in the other locations.
 */
export const getVisibleInventoryItems = (inventory, includeDamaged) => {
  const items = inventory?.inventoryItems?.items || [];
  return includeDamaged ? items : items.filter((item) => !item.isDamaged);
};

/**
 * The same filter applied down a list of inventories, dropping the ones with
 * nothing left to show.
 *
 * An inventory disappears only when every one of its items is damaged. One
 * damaged pallet in aisle B must not hide the good stock in aisle A.
 */
export const excludeDamagedInventories = (inventoryList, includeDamaged) => {
  // Always a list, on both paths: the callers map over the result, and a
  // not-yet-loaded list must not throw only when the checkbox happens to be on.
  if (includeDamaged) return inventoryList || [];

  return (inventoryList || []).reduce((acc, inventory) => {
    const items = inventory.inventoryItems?.items || [];
    const visibleItems = getVisibleInventoryItems(inventory, includeDamaged);
    // An inventory with no items at all (nothing placed yet) is not damaged,
    // it is just unplaced — keep it. Drop only the fully damaged ones.
    if (items.length > 0 && visibleItems.length === 0) return acc;

    // By design the parent's quantity columns (on hand, committed, available,
    // weight, pieces) are NOT re-derived from visibleItems. They are the true
    // position of the inventory and a manager needs to see it whole — hiding
    // the damaged units from the totals would misreport what is in the yard.
    // Selection is what stays honest: ticking the row picks only the good
    // items, so the pick can come up short of the number in the column.
    acc.push({
      ...inventory,
      // isDamaged on the parent is a roll-up from its items. Leaving it set
      // while the damaged items are filtered out would paint the row red with
      // nothing red left in it. Every visible item is undamaged on this path —
      // the includeDamaged case returned above — so the roll-up is always false.
      isDamaged: false,
      // hasException is what draws the amber glyph, and every line of that
      // glyph's tooltip is gated on one of the flags below. An inventory whose
      // only exception was the damage would keep drawing a warning with an
      // empty tooltip, so the roll-up is recomputed from what is left.
      //
      // The flags are the union of what the writers actually set it from, not
      // one writer's list: crew inbound save (utils.js) leaves isRecalled out
      // because a recall is raised later, but the manager exception dialog
      // (InventoryEditActions) and both inventoryItem forms fold it in. Drop it
      // here and a recalled inventory that also holds damage loses its recall
      // warning in the pick dialog while staying pickable -- the one exception
      // that most needs to reach the person building the shipment.
      hasException: Boolean(
        inventory.overShipment ||
        inventory.underShipment ||
        inventory.isRecalled ||
        inventory.otherException,
      ),
      inventoryItems: { ...inventory.inventoryItems, items: visibleItems },
    });
    return acc;
  }, []);
};

/**
 * Whether two inventoryItems may be combined into one row.
 *
 * Only clean stock merges. A merge keeps one item's damage fields and deletes
 * the other row, and an inventoryItem's damage record is more than the flag:
 * originOfDamage says who owes for the units (the export reports quantity per
 * origin), and otherExceptionNote can carry the claim's evidence. Merging two
 * damaged rows keeps only one side's copy of all of that — so a damaged row
 * never merges, not even with another damaged row. Damage is recorded per
 * event, and each event keeps its own row until the exception is resolved.
 * A refusal falls through to the move/create path and the item stays its own
 * row in the location.
 *
 * Read the flag loosely on purpose: undefined, null and false all mean "not
 * damaged", and the older inventoryItems in a yard predate the field.
 */
export const canMergeInventoryItems = (inventoryItem, otherInventoryItem) =>
  !inventoryItem?.isDamaged && !otherInventoryItem?.isDamaged;

/**
 * Whether a load may be placed into a bulk tank, by condition.
 *
 * A tank holds one pool of one product in one condition. Rows are bookkeeping
 * over that pool -- they cannot split what is physically one volume -- so the
 * damage rule for tanks is per tank, not per row: contaminated stock never
 * enters a clean, occupied tank, and clean stock never enters a contaminated
 * one. Contaminated may join contaminated: a claim per inbound still has its
 * own inventoryItem row (canMergeInventoryItems refuses to merge them).
 *
 * Contamination is physical, so it outlives the stock. Shipping a damaged pool
 * out empties the bookkeeping but leaves the residue in the tank, and the next
 * clean load would pick it up -- so `isContaminated` is checked before the tank
 * is empty, not after. Nothing in the inventory flow clears it: the tank is
 * clean again only when a manager says so, by clearing the tank's capacity in
 * the bulk location dialog, which is the yard's record that it was washed out.
 *
 * An empty tank that was never contaminated accepts either condition -- the
 * placement is what stamps the flag.
 */
export const canStoreInBulkTank = (location, isDamagedLoad) => {
  // Checked ahead of the occupancy test on purpose: a contaminated tank stays
  // closed to clean stock even at zero capacity.
  if (location?.isContaminated) return Boolean(isDamagedLoad);

  const occupied =
    (location?.capacityUsed || 0) > 0 || (location?.capacityAllocated || 0) > 0;
  if (!occupied) return true;

  // Clean and occupied: clean stock only.
  return !isDamagedLoad;
};
